import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { HiveMark, Icon, type IconName } from '@hive/ui';
import { api, type NetworkOverview, type Permission } from '../api/client';
import { PasswordDialog } from './PasswordDialog';
import { useAuth } from '../auth/AuthContext';
import { initials } from '../format';

const RAIL: { icon: IconName; label: string; to?: string; permission?: Permission }[] = [
  { icon: 'map', label: 'Map', to: '/network' },
  { icon: 'venues', label: 'Venues', to: '/venues', permission: 'venues.manage' },
  { icon: 'broadcast', label: 'Streams', to: '/streams', permission: 'streams.manage' },
  { icon: 'licence', label: 'Rights and licences' },
  { icon: 'screen', label: 'Screens' },
  { icon: 'bell', label: 'Alerts', to: '/alerts', permission: 'network.view' },
  { icon: 'reports', label: 'Reports' },
  { icon: 'users', label: 'Users', to: '/users', permission: 'users.manage' },
  { icon: 'key', label: 'Integrations', to: '/integrations', permission: 'integrations.manage' },
];

interface Props {
  network: NetworkOverview | null;
  live: boolean;
  children: ReactNode;
}

/** Open alerts for real venues, refreshed every 30 s, for the rail badge. */
function useOpenAlertCount(enabled: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    const load = () =>
      api
        .GET('/v1/alerts')
        .then(({ data }) => !stop && data && setCount(data.length))
        .catch(() => undefined);
    void load();
    const t = setInterval(load, 30_000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [enabled]);
  return count;
}

/** Nav rail + header (MC-Map / MC-Venue / MC-Screen). Rail items not in the prototype are shown disabled. */
export function Shell({ network, live, children }: Props) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const rail = RAIL.filter((item) => !item.permission || user?.permissions.includes(item.permission));
  const alerts = useOpenAlertCount(Boolean(user?.permissions.includes('network.view')));
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2 || !network) return [];
    return network.venues.filter((v) => [v.name, v.code, v.city].some((x) => x?.toLowerCase().includes(q))).slice(0, 8);
  }, [query, network]);
  const now = new Intl.DateTimeFormat('en-GB', { weekday: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short' }).format(new Date());

  return (
    <div className="hv-root mc-app">
      <nav aria-label="Master control" className="mc-rail">
        <Link to="/network" className="mc-rail__logo" aria-label="Hive master control">
          <HiveMark size={22} tone="white" />
        </Link>
        {rail.map((item) =>
          item.to ? (
            <NavLink key={item.label} to={item.to} aria-label={item.label} title={item.label} className={({ isActive }) => `mc-rail__item${isActive ? ' mc-rail__item--on' : ''}`}>
              <Icon name={item.icon} size={20} />
              {item.label === 'Alerts' && alerts > 0 && <span className="mc-rail__badge">{alerts}</span>}
            </NavLink>
          ) : (
            <span key={item.label} aria-label={`${item.label} (not in the prototype)`} title={`${item.label}: not in the prototype`} className="mc-rail__item mc-rail__item--off">
              <Icon name={item.icon} size={20} />
            </span>
          ),
        )}
        <span className="mc-rail__spacer" />
        <span aria-label="Settings (not in the prototype)" title="Settings: not in the prototype" className="mc-rail__item mc-rail__item--off">
          <Icon name="settings" size={20} />
        </span>
      </nav>
      <div className="mc-main">
        <header className="mc-header">
          <div className="mc-header__left">
            <span className="mc-header__title">Master control</span>
            <span className="hv-hairline--v mc-header__sep" />
            <span className="mc-tenant">
              <span aria-hidden="true" className="mc-tenant__mark">
                {initials(network?.tenant.name ?? user?.tenantName ?? '')}
              </span>
              {network?.tenant.name ?? user?.tenantName ?? '…'} · tenant
            </span>
            <span className="mc-live">
              <span className={`hv-dot${live ? ' hv-dot--healthy hv-dot--glow' : ' hv-dot--degraded'}`} />
              {live ? 'Live' : 'Reconnecting'} · {now}
            </span>
          </div>
          <div className="mc-header__right">
            <div className="mc-search">
              <label className="mc-search__box">
                <Icon name="search" size={16} />
                <span className="hv-sr-only">Search</span>
                <input type="search" placeholder="Search venues, screens, venue IDs" value={query} onChange={(e) => setQuery(e.target.value)} />
              </label>
              {results.length > 0 && (
                <ul className="mc-search__results" role="listbox">
                  {results.map((v) => (
                    <li key={v.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setQuery('');
                          navigate(`/venue/${v.id}`);
                        }}
                      >
                        <span>{v.name}</span>
                        <span className="hv-muted">
                          {v.city} · {v.code}
                          {v.source === 'simulated' ? ' · simulated' : ' · real'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {user && (
              <button type="button" className="mc-user" title="Change your password" onClick={() => setPasswordOpen(true)}>
                <span aria-hidden="true" className="mc-user__avatar">
                  {initials(user.name)}
                </span>
                {user.title ?? user.name}
              </button>
            )}
            <button type="button" className="hv-btn hv-btn--secondary hv-btn--sm" onClick={signOut}>
              Sign out
            </button>
          </div>
        </header>
        <div className="mc-body">{children}</div>
        {passwordOpen && <PasswordDialog onClose={() => setPasswordOpen(false)} />}
      </div>
    </div>
  );
}
